// ============================================================================
// 바인더 좌표 — 순수 계산. three.js 를 모른다.
//
// 설계서(§10)의 파일 목록에는 없는 모듈이다. **BinderSim(물리·히트테스트)과
// BinderView(렌더) 가 같은 칸 좌표를 써야 하는데**, sim 이 three.js 를 아는 assets.ts 를
// import 하면 "sim 은 three 를 모른다" 규칙이 깨진다. 그래서 좌표 수학만 여기로 뺐다.
//
// 좌표계 (전부 world unit, 카메라는 안 움직인다 — 바인더 루트가 -viewX 만큼 밀린다):
//   등(spine) x = 0.  오른쪽 더미 시트 중심 = +W/2, 왼쪽 = -W/2.
//   W = 화면 폭 - 2*neighborPeek (펼침 모드는 (화면 폭 - 2*spreadMargin)/2). 높이는 시트 그림의 종횡비에서.
//   가로로 넓은 화면은 **펼침 모드** — 좌우 두 면이 동시에 보이고 카메라는 등에 고정이다 (아래 '펼침' 절).
//   칸 UV 의 v 는 **이미지 위쪽 기준**이다 (0 = 시트 위 가장자리). 텍스처 v 는 1-v.
// ============================================================================
// .ts 확장자 — 헤드리스 도구가 이 파일(과 params.ts)을 Node 로 직접 import 할 수 있게 남겨 둔다.
import { P, WORLD } from './params.ts';
import { CARD_ASPECT } from './cards.ts';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface CellUV {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}
export interface BinderMeta {
  source: string; // 'fallback' | 'measured'
  cover: { rect: Rect; spineX: number; leftRect: Rect; rightRect: Rect };
  sheet: { rect: Rect; ringStripW: number; cells: Rect[]; cellsUV: CellUV[]; cellsUVBack: CellUV[]; cellRadius: number };
  gloss: { source: string };
}

/**
 * 시트/커버의 정규화 사각형 = **캔버스 전체**.
 *
 * **여백을 두면 안 된다.** 카드 위치(`cellBox`)는 `u0` 를 시트 메시 전체 기준으로 읽는데,
 * 슬리브 그림(`drawSheet`)은 이 rect 안에서 읽는다. rect 가 캔버스보다 작거나 밀려 있으면
 * 두 좌표계가 어긋나 **슬리브만 안쪽으로 줄고 카드는 제자리** → 바깥 열일수록 카드가
 * 슬리브 밖으로 벗어난다 (2026-09-10 사용자 보고 "좌우 카드가 슬리브 밖으로 나감").
 * 예전 값: SHEET x24 y16 w976 h1504 / COVER x16 y24 w1504 h976.
 */
// **시트 이미지의 실제 높이와 같아야 한다.** post-binder.mjs 가 칸 비율을 맞추려고
// 시트를 세로로 리샘플하므로 1504 로 박아 두면 메시가 그 비율로 그려져 텍스처가 눌린다
// (2026-09-14: 실제 1364 인데 1504 로 그려 화면 칸 비율이 1.398 → 1.269 로 눌렸다).
// 폴백 값일 뿐이고, binder.json 이 로드되면 setBinderMeta 가 실제 값으로 덮는다.
const SHEET_RECT: Rect = { x: 0, y: 0, w: 1024, h: 1364 };
const COVER_RECT: Rect = { x: 0, y: 0, w: 1536, h: 1024 };

/**
 * 폴백 텍스처를 그릴 캔버스 크기.
 *
 * **메시 비율(SHEET_RECT)에서 유도한다 — 상수로 박으면 안 된다.** 예전엔 1024x1536(비 1.5)
 * 고정이었는데 시트 메시는 1504/976 = 1.541 이라, 텍스처가 세로로 2.7% 늘어나면서
 * 슬리브 그림만 아래로 밀리고 카드(코드 좌표)는 제자리에 남아 어긋나 보였다 (2026-09-10).
 */
export const SHEET_PX = { w: 1024, h: Math.round((1024 * SHEET_RECT.h) / SHEET_RECT.w) };
export const COVER_PX = { w: 1536, h: Math.round((1536 * COVER_RECT.h) / COVER_RECT.w) };

/** 뒷면에서도 칸 0 이 왼쪽 위여야 하므로 열 순서를 뒤집는다 (§5.4). */
export function mirrorIndex(i: number): number {
  return i - (i % 3) + (2 - (i % 3));
}

function mirrorCells(cells: CellUV[]): CellUV[] {
  const out: CellUV[] = [];
  for (let i = 0; i < 9; i++) {
    const c = cells[mirrorIndex(i)];
    out.push({ u0: 1 - c.u1, v0: c.v0, u1: 1 - c.u0, v1: c.v1 });
  }
  return out;
}

/**
 * 폴백 좌표 — 링 띠 7%, 포켓 폭 28%.
 *
 * **가로·세로 모두 남는 공간을 균등 분배한다.** 예전엔 세로만 `vGap` 으로 나누고
 * 가로는 `ring + margin + c*(pw+gap)` 으로 왼쪽부터 쌓기만 해서 오른쪽 여백이 남았고,
 * 칸이 오른쪽으로 갈수록 카드가 밀려 보였다 (2026-09-10 사용자 보고).
 *
 * 카드 종횡비는 **레지스트리에서 온다** — 여기 상수로 박으면 그림이 바뀔 때 또 어긋난다.
 */
function makeFallbackMeta(): BinderMeta {
  const ring = 0.07,
    pw = 0.28;
  const cardAspect = CARD_ASPECT;
  // 칸 높이는 카드 종횡비에서. 시트 rect 의 px 비율로 UV 로 환산한다.
  const ph = (pw * SHEET_RECT.w * cardAspect) / SHEET_RECT.h;
  const vGap = (1 - 3 * ph) / 4;
  // 가로도 같은 방식: 링 띠를 뺀 나머지에서 칸 3개를 빼고 4등분한다.
  const hGap = (1 - ring - 3 * pw) / 4;
  const cells: CellUV[] = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const u0 = ring + hGap + c * (pw + hGap);
      const v0 = vGap + r * (ph + vGap);
      cells.push({ u0, v0, u1: u0 + pw, v1: v0 + ph });
    }
  }
  const px = (c: CellUV): Rect => ({
    x: Math.round(SHEET_RECT.x + c.u0 * SHEET_RECT.w),
    y: Math.round(SHEET_RECT.y + c.v0 * SHEET_RECT.h),
    w: Math.round((c.u1 - c.u0) * SHEET_RECT.w),
    h: Math.round((c.v1 - c.v0) * SHEET_RECT.h),
  });
  return {
    source: 'fallback',
    cover: {
      rect: { ...COVER_RECT },
      spineX: COVER_RECT.x + COVER_RECT.w / 2,
      leftRect: { x: COVER_RECT.x, y: COVER_RECT.y, w: COVER_RECT.w / 2, h: COVER_RECT.h },
      rightRect: { x: COVER_RECT.x + COVER_RECT.w / 2, y: COVER_RECT.y, w: COVER_RECT.w / 2, h: COVER_RECT.h },
    },
    sheet: {
      rect: { ...SHEET_RECT },
      ringStripW: ring,
      cells: cells.map(px),
      cellsUV: cells,
      cellsUVBack: mirrorCells(cells),
      cellRadius: 14,
    },
    gloss: { source: 'procedural' },
  };
}

export const FALLBACK_BINDER_META: BinderMeta = makeFallbackMeta();

let meta: BinderMeta = FALLBACK_BINDER_META;
let metaVersion = 0;

export function binderMeta(): BinderMeta {
  return meta;
}
/** 측정된 좌표가 도착했을 때. 폴백 좌표로 배치한 칸·배지는 **다시 배치**해야 한다 (§5.8 함정). */
export function setBinderMeta(m: BinderMeta): void {
  meta = m;
  metaVersion++;
}
export function binderMetaVersion(): number {
  return metaVersion;
}

/** 유효성 검사 — binder.json 이 깨졌으면 폴백을 유지한다. */
export function isValidMeta(m: any): m is BinderMeta {
  return (
    !!m &&
    m.sheet &&
    m.sheet.rect &&
    Array.isArray(m.sheet.cellsUV) &&
    m.sheet.cellsUV.length === 9 &&
    Array.isArray(m.sheet.cellsUVBack) &&
    m.sheet.cellsUVBack.length === 9 &&
    m.cover &&
    typeof m.cover.spineX === 'number'
  );
}

// ---------------------------------------------------------------- 크기
/** 화면 폭 (unit). WORLD.viewH 는 손맛 상수라 건드리지 않는다. */
export function screenWidth(aspect: number): number {
  return WORLD.viewH * aspect;
}
/**
 * 면(시트) 폭. 한쪽엔 펼침의 반대 면, 다른 쪽엔 커버가 neighborPeek 만큼 걸친다.
 *
 * **가로·세로 둘 다에 맞춘다.** 폭만 보고 정하면 가로로 넓은 창(데스크톱 전체화면)에서
 * 높이가 viewH 를 넘어 시트가 위아래로 잘린다. 세로 폰에서는 폭이, 가로 화면에서는
 * 높이가 제약이 된다. 세로 여백은 binder.frameY 아래 위로 같이 남는다.
 */
export function faceWidth(aspect: number): number {
  // 펼침 모드는 두 면이 가로로 나란히 들어가야 한다: 2W + 2*spreadMargin <= 화면 폭.
  // 포트레이트는 한 면 + 양쪽 peek: W + 2*neighborPeek <= 화면 폭.
  const byWidth = isSpread(aspect)
    ? (screenWidth(aspect) - 2 * Math.max(0, P.binder.spreadMargin)) / 2
    : screenWidth(aspect) - 2 * Math.max(0, P.binder.neighborPeek);
  // 높이 제약: faceHeight = W * sheetAspect <= viewH * fitH  (두 모드 공통)
  const byHeight = (WORLD.viewH * Math.max(0.1, P.binder.fitH)) / Math.max(0.01, sheetAspect());
  return Math.max(0.5, Math.min(byWidth, byHeight));
}
export function sheetAspect(): number {
  const r = meta.sheet.rect;
  return r.h / r.w;
}
export function faceHeight(aspect: number): number {
  return faceWidth(aspect) * sheetAspect();
}
/** 정착한 면의 중심 x. 홀수 면 = 오른쪽 더미(+W/2), 짝수 면 = 왼쪽(-W/2). */
export function faceCenterX(face: number, W: number): number {
  return face % 2 === 1 ? W / 2 : -W / 2;
}

// ---------------------------------------------------------------- 펼침 (와이드 화면)
/**
 * 가로로 넓은 화면에서는 실물 바인더처럼 **좌우 두 면을 동시에** 보여준다.
 *
 * **펼침(spread) k = 왼쪽 면 2k / 오른쪽 면 2k+1 의 쌍.** k = 0..S (S = 시트 수 = F/2).
 *   k = 0 : 왼쪽 면이 없다 → 왼쪽 커버 속면 + 면 1   (실물 바인더의 첫 장 그대로)
 *   k = S : 오른쪽 면이 없다 → 면 F + 오른쪽 커버 속면 (마지막 장)
 * 면 f 가 속한 펼침은 floor(f/2) — 홀수 2k+1 과 짝수 2k 가 같은 k 로 간다.
 * 그래서 **모드를 바꿔도 보던 면이 유지된다** (포트레이트의 면 f → 그 f 가 든 펼침).
 *
 * 한 번 넘기면 **두 면이 한꺼번에** 바뀐다 (k → k±1). 그래서 펼침의 넘기기는
 * **항상 flip 이다 — pan 이 없다**: 다음(k+1) = 시트 k+1 을 왼쪽으로 뒤집기,
 * 이전(k-1) = 시트 k 를 오른쪽으로 되뒤집기. 포트레이트의 홀짝 교대(flip/pan)가
 * 사라지는 것도 실물과 같다 (실물 바인더를 펼쳐 놓고 보면 밀 일이 없다).
 *
 * 렌더의 역할 배정(sLeft = floor(face/2), sRight = sLeft+1)은 2k 와 2k+1 에 대해
 * **같은 값**이라 펼침에서도 그대로 맞는다. 바뀌는 것은 카메라(viewXOf)와 폭(faceWidth)뿐이다.
 */
export function isSpread(aspect: number): boolean {
  return aspect >= Math.max(0.2, P.binder.spreadAspect);
}
export function spreadOf(face: number): number {
  return Math.floor(face / 2);
}
/** 펼침 k 의 왼쪽 면. 0 = 없음 (왼쪽 커버 속면). */
export function spreadLeftFace(k: number): number {
  return 2 * k;
}
/** 펼침 k 의 오른쪽 면. 0 = 없음 (오른쪽 커버 속면). */
export function spreadRightFace(k: number, F: number): number {
  const f = 2 * k + 1;
  return f <= F ? f : 0;
}
/** 펼침 k 의 대표 면 — `BinderSim.face` 에 넣는 값. 오른쪽 면이 있으면 그것, 없으면 왼쪽 면. */
export function spreadCanonFace(k: number, F: number): number {
  return 2 * k + 1 <= F ? 2 * k + 1 : 2 * k;
}

export type SegKindId = 0 | 1 | 2 | 3; // 0 none, 1 flip, 2 pan, 3 edge
export const SEG_NONE = 0,
  SEG_FLIP = 1,
  SEG_PAN = 2,
  SEG_EDGE = 3;

/** flip 의 시트 각도. dir=+1 이면 0→pi, dir=-1 이면 pi→0. */
export function thetaOf(u: number, dir: number): number {
  return (dir > 0 ? u : 1 - u) * Math.PI;
}

/**
 * 카메라(= 바인더 루트의 반대 방향) 위치. **u 의 함수다 — 절대 따로 적분하지 않는다.**
 * flip 에서 도는 시트 중심의 x 투영을 그대로 따라가므로 "방금 뒤집힌 면이 중앙" 이 식 하나에서 나온다.
 */
export function viewXOf(kind: SegKindId, dir: number, u: number, face: number, W: number, spread = false): number {
  // 펼침에서는 **카메라가 면 단위로 움직이지 않는다.** 펼침 전체가 화면에 들어오고
  // 등(spine)이 화면 중앙(x=0)에 고정이다 — 실물 책처럼 책은 가만히 있고 장만 넘어간다.
  // 펼침이 바뀌어도 좌우 면은 같은 자리(-W/2 / +W/2)에 오므로 움직일 이유가 없다.
  // u 에 대해 상수 함수이므로 "카메라는 u 의 함수" 규칙은 자동으로 지켜진다 (어긋날 수가 없다).
  // 끝 펼침의 저항만 살짝 밀린다.
  if (spread) return kind === SEG_EDGE ? dir * u * P.binder.edgeSlide : 0;
  switch (kind) {
    case SEG_FLIP:
      return (W / 2) * Math.cos(thetaOf(u, dir));
    case SEG_PAN:
      return dir > 0 ? -W / 2 + u * W : W / 2 - u * W;
    case SEG_EDGE:
      return faceCenterX(face, W) + dir * u * P.binder.edgeSlide;
    default:
      return faceCenterX(face, W);
  }
}

// ---------------------------------------------------------------- 칸
export interface CellBox {
  cx: number; // 시트 홀더 로컬 중심
  cy: number;
  w: number;
  h: number;
}

/** 칸 i 의 홀더 로컬 사각형. back 이면 cellsUVBack (뒷면 UV 를 안 쓰면 카드가 반대 열에 붙는다). */
export function cellBox(i: number, back: boolean, W: number, H: number, out: CellBox): CellBox {
  const c = (back ? meta.sheet.cellsUVBack : meta.sheet.cellsUV)[i];
  out.w = (c.u1 - c.u0) * W;
  out.h = (c.v1 - c.v0) * H;
  out.cx = ((c.u0 + c.u1) / 2 - 0.5) * W;
  out.cy = (0.5 - (c.v0 + c.v1) / 2) * H;
  return out;
}

/** 칸 안에 들어가는 카드 크기 (cardInset 만큼 줄이고 카드 종횡비를 유지, 짧은 변에 맞춤). */
export function cardSizeInCell(box: CellBox, cardAspect: number, out: { w: number; h: number }): { w: number; h: number } {
  // **음수를 허용한다** — 칸보다 살짝 크게 넣어야 실물처럼 꽉 낀 느낌이 난다.
  // 0 으로 막으면 칸 비율(1.574)이 카드(1.512)보다 세로로 길어서 카드가 항상 작아 보인다.
  const inset = Math.max(-0.05, Math.min(0.45, P.binder.cardInset));
  const aw = box.w * (1 - 2 * inset);
  const ah = box.h * (1 - 2 * inset);
  if (aw * cardAspect <= ah) {
    out.w = aw;
    out.h = aw * cardAspect;
  } else {
    out.h = ah;
    out.w = ah / cardAspect;
  }
  return out;
}

/** 카메라 거리 (render.ts resize() 와 같은 식). z 가 0 이 아닌 평면의 화면 배율에 쓴다. */
export function cameraDistance(): number {
  return WORLD.viewH / 2 / Math.tan((WORLD.cameraFov * Math.PI) / 360);
}
/** z 평면의 화면 확대 배율. 화면상 크기를 정하려면 메시 크기를 이 값으로 나눈다. */
export function perspScale(z: number): number {
  const d = cameraDistance();
  return d / Math.max(0.5, d - z);
}
